import { useEffect, useState, useCallback } from "react";
import { Trophy, RefreshCw, Flag } from "lucide-react";
import api from "@/lib/api";
import { PageShell } from "@/components/PageShell";

export default function Leaderboard() {
  const [rows, setRows] = useState([]);
  const [updated, setUpdated] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const { data } = await api.get("/leaderboard");
    setRows(data.rows);
    setUpdated(data.updated_at);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const toPar = (n) => (n === 0 ? "E" : n > 0 ? `+${n}` : `${n}`);
  const parColor = (n) => (n < 0 ? "text-green-600" : n > 0 ? "text-red-500" : "text-slate-600");

  return (
    <PageShell>
      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-16">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-green-600">
              <span className="h-2.5 w-2.5 rounded-full bg-green-500 live-dot" />
              <span className="text-sm font-semibold uppercase tracking-widest">Live</span>
            </div>
            <h1 className="mt-2 font-display text-4xl lg:text-5xl text-slate-900">Leaderboard</h1>
            <p className="mt-1 text-slate-500 text-sm">Auto-refreshing · {updated ? new Date(updated).toLocaleTimeString() : "—"}</p>
          </div>
          <button data-testid="refresh-leaderboard" onClick={load} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-100 font-semibold hover:bg-slate-200 transition-colors">
            <RefreshCw size={16} /> Refresh
          </button>
        </div>

        <div className="mt-8 rounded-3xl border border-slate-100 bg-white overflow-hidden shadow-sm">
          <div className="grid grid-cols-12 gap-2 px-6 py-4 bg-slate-900 text-white text-xs uppercase tracking-wider font-semibold">
            <div className="col-span-2">Pos</div>
            <div className="col-span-5">Team</div>
            <div className="col-span-2 text-center">Holes</div>
            <div className="col-span-3 text-right">To Par</div>
          </div>
          {loading ? (
            <div className="p-10 space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-10 bg-slate-100 rounded-xl animate-pulse" />)}</div>
          ) : rows.length === 0 ? (
            <div className="p-16 text-center text-slate-500">
              <Flag className="mx-auto mb-3 text-slate-300" size={40} />
              Scoring hasn't started yet. Check back on event day!
            </div>
          ) : (
            rows.map((r) => (
              <div key={r.team_name} data-testid={`lb-row-${r.position}`} className="grid grid-cols-12 gap-2 px-6 py-4 items-center border-t border-slate-50 hover:bg-sky-50/50 transition-colors">
                <div className="col-span-2">
                  <span className={`inline-grid place-items-center h-8 w-8 rounded-full font-bold text-sm ${r.position === 1 ? "bg-yellow-400 text-white" : r.position <= 3 ? "bg-sky-100 text-sky-700" : "bg-slate-100 text-slate-600"}`}>
                    {r.position === 1 ? <Trophy size={15} /> : r.position}
                  </span>
                </div>
                <div className="col-span-5 font-semibold text-slate-900">{r.team_name}</div>
                <div className="col-span-2 text-center text-slate-600">{r.holes_played}/18</div>
                <div className={`col-span-3 text-right font-bold text-lg ${parColor(r.to_par)}`}>{toPar(r.to_par)}</div>
              </div>
            ))
          )}
        </div>
      </div>
    </PageShell>
  );
}
